import React, { useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@hub/popover';
import { Drawer, DrawerOverlay, DrawerContent, DrawerBody, DrawerCloseButton } from '@hub/drawer';
import { DEFAULT_BREAKPOINT,
// eslint-disable-next-line no-restricted-syntax
useBreakpointValue // The 'useBreakpointValue' is utilized on the client side only after user interaction, ensuring that it does not pose any issues.
} from '@hub/design-system-base';
import { useDisclosure } from '@hub/use-disclosure';
import { Box } from '@hub/box';
import { Stack } from '@hub/stack';
import { ChevronDownSmallIcon, ChevronUpSmallIcon } from '@hub/icon';
import ResponsiveButton from './responsive-button';
import { StandardButton } from '../standard-button';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ActionSheetTrigger = () => null;
export const ActionSheetContent = () => null;
export const ActionSheetCTA = () => null;
export const ActionSheet = props => {
  var _useBreakpointValue, _triggerChildProps$ga, _triggerChildProps$ga2;
  const {
    0: ctaChild,
    1: triggerChildProps,
    2: contentChild
  } = useMemo(() => {
    let triggerChildProps = {};
    let contentChild = null;
    let ctaChild = null;
    React.Children.forEach(props.children, child => {
      if (! /*#__PURE__*/React.isValidElement(child)) {
        return;
      }
      if (child.type === ActionSheetTrigger) {
        triggerChildProps = child.props;
      }
      if (child.type === ActionSheetContent) {
        contentChild = child.props.children;
      }
      if (child.type === ActionSheetCTA) {
        ctaChild = child.props.children;
      }
    });
    return [ctaChild, triggerChildProps, contentChild];
  }, [props.children]);
  const {
    onOpen,
    onClose,
    isOpen
  } = useDisclosure();
  const isMobile = (_useBreakpointValue = useBreakpointValue([true, true, false], DEFAULT_BREAKPOINT)) !== null && _useBreakpointValue !== void 0 ? _useBreakpointValue : false;
  const handleClick = () => {
    var _props$onClick;
    const onClickValue = (_props$onClick = props.onClick) === null || _props$onClick === void 0 ? void 0 : _props$onClick.call(props);
    if (onClickValue === undefined || onClickValue) {
      toggleOpen();
    }
  };
  const toggleOpen = () => {
    if (isOpen) {
      var _props$onClose;
      (_props$onClose = props.onClose) === null || _props$onClose === void 0 || _props$onClose.call(props);
      onClose();
    } else {
      var _props$onOpen;
      (_props$onOpen = props.onOpen) === null || _props$onOpen === void 0 || _props$onOpen.call(props);
      onOpen();
    }
  };
  return /*#__PURE__*/_jsxs(Box, {
    "data-id": "action-sheet_index_ActionSheet_Box",
    children: [/*#__PURE__*/_jsxs(Popover, {
      isOpen: !isMobile && isOpen,
      onClose: toggleOpen,
      onOpen: toggleOpen,
      placement: "bottom-start",
      matchWidth: true,
      "data-id": "action-sheet_index_ActionSheet_Box_Popover",
      children: [/*#__PURE__*/_jsx(PopoverTrigger, {
        "data-id": "action-sheet_index_ActionSheet_Popover_PopoverTrigger",
        children: /*#__PURE__*/_jsx(ResponsiveButton, {
          "data-id": "action-sheet_index_ActionSheet_PopoverTrigger_ResponsiveButton",
          children: /*#__PURE__*/_jsxs(Stack, {
            direction: "row",
            align: "center",
            shouldWrapChildren: false,
            gap: (_triggerChildProps$ga = triggerChildProps.gap) !== null && _triggerChildProps$ga !== void 0 ? _triggerChildProps$ga : 'spacing-md',
            sx: {
              flexGrow: '1',
              paddingTop: 'spacing-sm',
              paddingBottom: 'spacing-sm',
              paddingLeft: [0, null, 'spacing-md'],
              paddingRight: [0, null, 'spacing-md']
            },
            "data-id": "action-sheet_index_ActionSheet_ResponsiveButton_Stack",
            children: [/*#__PURE__*/_jsx(Stack, {
              direction: "row",
              textAlign: "left",
              alignItems: "center",
              shouldWrapChildren: false,
              flexGrow: 1,
              gap: (_triggerChildProps$ga2 = triggerChildProps.gap) !== null && _triggerChildProps$ga2 !== void 0 ? _triggerChildProps$ga2 : 'spacing-sm',
              "data-id": "action-sheet_index_ActionSheet_Stack_Stack_5",
              children: triggerChildProps.children
            }), isOpen ? /*#__PURE__*/_jsx(ChevronUpSmallIcon, {
              sx: {
                flexGrow: 0,
                display: ['none', null, 'block']
              },
              "data-id": "action-sheet_index_ActionSheet_ChevronUpSmallIcon"
            }) : /*#__PURE__*/_jsx(ChevronDownSmallIcon, {
              sx: {
                flexGrow: 0,
                display: ['none', null, 'block']
              },
              "data-id": "action-sheet_index_ActionSheet_ChevronDownSmallIcon"
            })]
          })
        })
      }), /*#__PURE__*/_jsx(PopoverContent, {
        borderColor: "borderSecondary",
        borderRadius: 0,
        boxShadow: "none !important",
        "data-id": "action-sheet_index_ActionSheet_Popover_PopoverContent",
        children: /*#__PURE__*/_jsxs(Box, {
          padding: "spacing-md",
          sx: {
            maxHeight: '80vh',
            overflowY: 'auto'
          },
          "data-id": "action-sheet_index_ActionSheet_PopoverContent_Box",
          children: [/*#__PURE__*/_jsx(Box, {
            sx: {
              marginBottom: 'spacing-md'
            },
            "data-id": "action-sheet_index_ActionSheet_Box_Box_10",
            children: contentChild
          }), /*#__PURE__*/_jsx(StandardButton, {
            variant: "solid",
            onClick: handleClick,
            sx: {
              width: '100%'
            },
            "data-id": "action-sheet_index_ActionSheet_Box_StandardButton",
            children: ctaChild
          })]
        })
      })]
    }), /*#__PURE__*/_jsxs(Drawer, {
      placement: "bottom",
      onClose: toggleOpen,
      isOpen: isOpen && isMobile,
      size: "xl",
      "data-id": "action-sheet_index_ActionSheet_Box_Drawer",
      children: [/*#__PURE__*/_jsx(DrawerOverlay, {
        sx: {
          background: 'rgba(0, 0, 0, 0.6)'
        },
        "data-id": "action-sheet_index_ActionSheet_Drawer_DrawerOverlay"
      }), /*#__PURE__*/_jsxs(DrawerContent, {
        "data-id": "action-sheet_index_ActionSheet_Drawer_DrawerContent",
        children: [/*#__PURE__*/_jsx(Box, {
          style: {
            position: 'relative'
          },
          "data-id": "action-sheet_index_ActionSheet_DrawerContent_Box",
          children: /*#__PURE__*/_jsx(DrawerCloseButton, {
            borderColor: "borderSecondary",
            borderRadius: 0,
            boxShadow: "none !important",
            sx: {
              marginX: 'spacing-mobile-margin',
              marginY: 'spacing-md',
              position: 'absolute',
              top: 0,
              right: 0
            },
            "data-id": "action-sheet_index_ActionSheet_Box_DrawerCloseButton"
          })
        }), /*#__PURE__*/_jsxs(DrawerBody, {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: 'spacing-2xl',
            paddingTop: 'spacing-3xl',
            paddingLeft: 'spacing-mobile-margin',
            paddingRight: 'spacing-mobile-margin',
            paddingBottom: 'spacing-mobile-margin',
            backgroundColor: 'surfaceBrandPrimary'
          },
          "data-id": "action-sheet_index_ActionSheet_DrawerContent_DrawerBody",
          children: [/*#__PURE__*/_jsx(Box, {
            "data-id": "action-sheet_index_ActionSheet_DrawerBody_Box",
            children: contentChild
          }), /*#__PURE__*/_jsx(Box, {
            "data-id": "action-sheet_index_ActionSheet_DrawerBody_Box_19",
            children: /*#__PURE__*/_jsx(StandardButton, {
              variant: "solid",
              onClick: handleClick,
              "data-id": "action-sheet_index_ActionSheet_Box_StandardButton",
              children: ctaChild
            })
          })]
        })]
      })]
    })]
  });
};